import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { ConfirmDialog, errorList } from './shared';

/*
 * A media ÚNICA do exercício — POST/DELETE /api/library/exercises/{id}/media.
 *
 * Um só ficheiro (imagem ou vídeo) que substitui o que o ginásio tinha carregado; o servidor devolve o
 * exercício inteiro já com a media nova, para o ecrã não ter de voltar a pedi-lo. As imagens do catálogo
 * não se tocam. Um vídeo entra POR APROVAR: só chega aos atletas depois de uma pessoa o aprovar, e o ecrã
 * di-lo sempre (um ecrã que promete o vídeo ao atleta e o deixa pendente mente).
 *
 * O tecto de 25 MB é o mesmo do servidor (LibraryMediaService): recusa-se aqui antes de gastar o envio, com
 * uma mensagem que diz o tamanho do ficheiro e o que fazer. O tipo também se confere antes (um `.mov` do
 * iPhone é recusado com o que usar em vez dele, não em silêncio).
 *
 * Props:
 *   api         exerciseLibrary (de services/api)
 *   exerciseId  id do exercício (já gravado)
 *   media       lista actual (detail.media)
 *   onChange    (detalhe) => void — o exercício inteiro que o servidor devolveu
 *   canEdit     falso → só mostra
 */

const MAX_BYTES = 25 * 1024 * 1024;
const MAX_MB = MAX_BYTES / 1024 / 1024;
const ACCEPT = 'video/mp4,video/webm,image/jpeg,image/png,image/webp';

const KIND_BY_TYPE = {
  'video/mp4': 'video', 'video/webm': 'video',
  'image/jpeg': 'photo', 'image/png': 'photo', 'image/webp': 'photo',
};
const KIND_BY_EXT = { mp4: 'video', webm: 'video', jpg: 'photo', jpeg: 'photo', png: 'photo', webp: 'photo' };

// O estado da validação, tal como o servidor o devolve: o rótulo, a cor do selo e o que isso quer dizer.
const STATUS = {
  approved: { tone: 'ok', label: 'Aprovada', hint: 'Já é mostrada aos atletas.' },
  pending: { tone: 'warn', label: 'Pendente de aprovação', hint: 'Os atletas ainda não a veem: falta a aprovação de uma pessoa.' },
  rejected: { tone: 'bad', label: 'Rejeitada', hint: 'Não é mostrada aos atletas.' },
};

const mb = (bytes) => `${(bytes / 1024 / 1024).toFixed(1).replace('.', ',')} MB`;

/** O tipo do ficheiro, pelo MIME ou pela extensão (há navegadores que não dão MIME nenhum). */
const kindOf = (file) => {
  if (!file) return null;
  if (KIND_BY_TYPE[file.type]) return KIND_BY_TYPE[file.type];
  const ext = (file.name || '').split('.').pop().toLowerCase();
  return KIND_BY_EXT[ext] || null;
};

/** O endereço local da pré-visualização; em ambientes sem `createObjectURL` fica o nome do ficheiro. */
const objectUrl = (file) => {
  try {
    return typeof URL.createObjectURL === 'function' ? URL.createObjectURL(file) : null;
  } catch {
    return null;
  }
};

/** O erro real do envio, como o servidor o descreve — e, quando o corpo é grande demais, o motivo é o tamanho. */
const uploadError = (error, video) => {
  if (error?.response?.status === 413) {
    return `O ficheiro é maior do que o servidor aceita (${MAX_MB} MB). Encurte ou comprima o vídeo antes de o carregar.`;
  }
  return errorList(error, video ? 'Não foi possível carregar o vídeo.' : 'Não foi possível carregar a imagem.').join(' ');
};

export default function ExerciseMediaUpload({ api, exerciseId, media = [], canEdit = true, onChange }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(null);
  const [error, setError] = useState(null);
  const [local, setLocal] = useState(null);   // { url, kind, name } — a pré-visualização do que está a ser enviado
  const [alt, setAlt] = useState('');
  const [toRemove, setToRemove] = useState(false);

  // O ficheiro que este contrato governa: o que o ginásio carregou (o mais recente, se houver mais do que um).
  const uploaded = (media || [])
    .filter((m) => m.source === 'upload' && m.kind !== 'embed')
    .sort((a, b) => (b.id || 0) - (a.id || 0))[0] || null;

  // Um endereço local é memória do browser: liberta-se quando deixa de ser mostrado (e ao sair do ecrã).
  useEffect(() => () => { if (local?.url) URL.revokeObjectURL?.(local.url); }, [local]);

  const previewUrl = local?.url || uploaded?.url || null;
  const previewKind = local ? local.kind : uploaded?.kind;
  const status = uploaded ? STATUS[uploaded.validationStatus] || null : null;
  const videoPending = uploaded?.kind === 'video' && uploaded.validationStatus !== 'approved';

  const resetInput = () => { if (inputRef.current) inputRef.current.value = ''; };

  const upload = async (file) => {
    setError(null);
    if (!file) return;

    const kind = kindOf(file);
    if (!kind) {
      setError('Tipo de ficheiro não permitido. Use um vídeo MP4 ou WEBM, ou uma imagem JPEG, PNG ou WEBP.');
      resetInput();
      return;
    }
    if (file.size > MAX_BYTES) {
      setError(`O ficheiro tem ${mb(file.size)}; o máximo é ${MAX_MB} MB. Encurte ou comprima o vídeo antes de o carregar.`);
      resetInput();
      return;
    }

    setLocal({ url: objectUrl(file), kind, name: file.name });
    setBusy(true);
    setProgress(0);
    try {
      const { data } = await api.uploadSingleMedia(exerciseId, file, {
        alt: alt.trim() || undefined,
        // A percentagem só aparece quando o navegador a sabe (não em envios minúsculos).
        onProgress: (e) => { if (e?.total) setProgress(Math.min(100, Math.round((e.loaded / e.total) * 100))); },
      });
      if (onChange) onChange(data);
      setLocal(null);
      setAlt('');
      toast.success(kind === 'video' ? 'Vídeo carregado: fica à espera de aprovação' : 'Imagem carregada');
    } catch (e) {
      setError(uploadError(e, kind === 'video'));
      setLocal(null);
    } finally {
      setBusy(false);
      setProgress(null);
      resetInput();
    }
  };

  const remove = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.removeSingleMedia(exerciseId);
      // O servidor apagou os ficheiros que o ginásio tinha; pedir o exercício de novo evita um ecrã a mentir
      // (a imagem antiga e a demonstração em uso vêm na resposta).
      let detail = null;
      try {
        const { data } = await api.get(exerciseId);
        detail = data;
      } catch {
        // Sem a resposta, a lista local perde o ficheiro na mesma; o aviso abaixo diz que foi apagado.
      }
      if (onChange) onChange(detail);
      toast.success('Ficheiro removido');
    } catch (e) {
      setError(errorList(e, 'Não foi possível remover o ficheiro.').join(' '));
    } finally {
      setBusy(false);
      setToRemove(false);
    }
  };

  return (
    <div className="lib-media-upload" data-testid="exercise-media-upload">
      <h3>Vídeo ou imagem do exercício</h3>
      <span className="lib-hint">
        Um ficheiro que substitui o que o ginásio tinha carregado. As imagens do catálogo ficam.
      </span>

      {error && <div className="lib-notice bad" role="alert" style={{ marginTop: 10 }}>{error}</div>}

      {videoPending && (
        <div className="lib-notice warn" role="status" data-testid="media-pending-notice" style={{ marginTop: 10 }}>
          <strong>{uploaded.validationStatus === 'rejected' ? 'Vídeo rejeitado.' : 'Vídeo à espera de aprovação.'}</strong>{' '}
          {uploaded.validationStatus === 'rejected'
            ? 'Não é mostrado aos atletas.'
            : 'Só depois de uma pessoa o aprovar é que os atletas o veem.'}
        </div>
      )}

      <div className="lib-media-upload-body">
        <div className="lib-media-upload-preview">
          {previewUrl ? (
            previewKind === 'video' ? (
              <video src={previewUrl} muted loop playsInline autoPlay preload="metadata"
                aria-label={uploaded?.altText || local?.name || 'Pré-visualização do vídeo do exercício'}
                data-testid="media-preview-video" />
            ) : (
              <img src={previewUrl} alt={uploaded?.altText || local?.name || 'Pré-visualização da imagem do exercício'}
                data-testid="media-preview-image" />
            )
          ) : (
            <div className="lib-media-upload-empty">Ainda sem vídeo nem imagem carregados pelo ginásio.</div>
          )}
        </div>

        <div className="lib-media-upload-side">
          {uploaded && (
            <div className="lib-chips">
              <span className="lib-chip">{uploaded.kind === 'video' ? 'Vídeo' : 'Imagem'}</span>
              {status && <span className={`lib-chip ${status.tone}`} data-testid="media-status">{status.label}</span>}
              {uploaded.sizeBytes ? <span className="lib-chip">{mb(uploaded.sizeBytes)}</span> : null}
            </div>
          )}
          {status && <span className="lib-hint">{status.hint}</span>}

          {canEdit && (
            <>
              <label className="lib-label" htmlFor="ex-media-alt">Texto alternativo (opcional)</label>
              <input id="ex-media-alt" className="lib-input" value={alt} maxLength={200} disabled={busy}
                placeholder="Ex.: Agachamento com barra — vista de lado"
                onChange={(e) => setAlt(e.target.value)} />

              <div className="lib-media-upload-actions">
                <button type="button" className="lib-btn primary" disabled={busy} onClick={() => inputRef.current?.click()}>
                  Carregar vídeo ou imagem
                </button>
                {uploaded && (
                  <button type="button" className="lib-btn danger" disabled={busy} onClick={() => setToRemove(true)}>
                    Remover
                  </button>
                )}
              </div>
              <input ref={inputRef} id="ex-media-file" type="file" className="lib-sr-only" accept={ACCEPT}
                aria-label="Ficheiro para carregar" disabled={busy} onChange={(e) => upload(e.target.files?.[0])} />
            </>
          )}

          {busy && (
            <span className="lib-hint" role="status" data-testid="media-upload-progress">
              {progress == null ? 'A carregar…' : `A carregar… ${progress}%`}
            </span>
          )}

          <span className="lib-hint">
            {`MP4, WEBM, JPEG, PNG ou WEBP até ${MAX_MB} MB. Um vídeo só chega aos atletas depois de aprovado.`}
          </span>
        </div>
      </div>

      {toRemove && (
        <ConfirmDialog
          title="Remover o vídeo ou a imagem?"
          message="Apaga os ficheiros que o ginásio carregou para este exercício. As imagens do catálogo ficam. Esta ação não se pode desfazer."
          confirmLabel="Remover" danger busy={busy}
          onConfirm={remove} onCancel={() => setToRemove(false)} />
      )}
    </div>
  );
}
